<template>
  <ColumnFilterCell
    v-model:open="isOpenMenu"
    :summary="summaryText"
    :active="hasActiveFilter"
    :hovered="hovered"
    test-id-prefix="column-filter-list"
    padding-upset
    @positioned="searchInput?.focus()"
    @closed="endEditing"
    @clear="clear"
  >
    <div :class="$style.listDropdown">
      <ColumnFilterDropdownToolbar :reset-disabled="!hasActiveFilter" @apply="isOpenMenu = false" @reset="clear">
        <SCASearchInput appearance="border" :show-clear-icon="listSearchTerm.length > 0" @clear="listSearchTerm = ''">
          <input
            ref="searchInput"
            v-model="listSearchTerm"
            type="text"
            :placeholder="$t('common.action.search')"
            test-id="column-filter-list-search"
            :class="$style.searchInput"
          />
        </SCASearchInput>
        <template #trailing>
          <ListConditionsPanel
            :rows="conditionRows"
            :disabled="!conditionsEnabled"
            :disabled-operators="conditionDisabledOperators"
            @change="changeConditions"
            @closed="flush"
            @submit="isOpenMenu = false"
          />
        </template>
      </ColumnFilterDropdownToolbar>

      <div :class="$style.listBody" :aria-busy="optionsSkeleton || undefined">
        <Transition
          :enter-from-class="$style.collapsed"
          :leave-to-class="$style.collapsed"
          :enter-active-class="$style.collapsibleAnimating"
          :leave-active-class="$style.collapsibleAnimating"
        >
          <div v-if="optionsView === 'skeleton'" key="skeleton" :class="$style.collapsible">
            <div :class="$style.collapsibleContent">
              <ListOptionsSkeleton />
            </div>
          </div>
          <div v-else-if="optionsView === 'options'" key="options" :class="[$style.collapsible, $style.optionsBlock]">
            <div :class="[$style.collapsibleContent, $style.optionsContent]">
              <ListFilterOption
                :label="$t('common.action.selectAll')"
                :checked="allSelected"
                :indeterminate="someSelected"
                test-id="column-filter-list-select-all"
                @change="toggleSelectAll()"
              />
              <ListFilterOption
                v-if="blankOptionVisible"
                :label="blankLabel"
                :checked="blankChecked"
                :search-term="listSearchTerm"
                test-id="column-filter-list-blank"
                @change="toggleBlank"
              />
              <div :class="$style.itemsList">
                <ListFilterOption
                  v-for="item in filteredOptions"
                  :key="item.id"
                  :label="item.label"
                  :color="item.color"
                  :checked="isSelected(item.id)"
                  :search-term="listSearchTerm"
                  :test-id="`column-filter-list-option-${item.id}`"
                  @change="(checked) => toggleItem(item.id, checked)"
                />
              </div>
            </div>
          </div>
          <div v-else-if="optionsView === 'empty'" key="empty" :class="$style.collapsible">
            <div :class="$style.collapsibleContent">
              <div :class="$style.empty" test-id="column-filter-list-empty">
                {{ $t("columnFilter.list.noOptions") }}
              </div>
            </div>
          </div>
        </Transition>
      </div>
    </div>
  </ColumnFilterCell>
</template>

<script setup lang="ts">
import { ref, watch } from "vue";

import SCASearchInput from "../../SCASearchInput.vue";
import ColumnFilterCell from "../ColumnFilterCell.vue";
import ColumnFilterDropdownToolbar from "../ColumnFilterDropdownToolbar.vue";
import type { ColFilterGroup } from "../filterModel";
import type { ListColumnFilterConfig } from "../types";
import ListConditionsPanel from "./ListConditionsPanel.vue";
import ListFilterOption from "./ListFilterOption.vue";
import ListOptionsSkeleton from "./ListOptionsSkeleton.vue";
import { useListFilter } from "./useListFilter";

const props = defineProps<{
  colKey: string;
  config: ListColumnFilterConfig;
  model: ColFilterGroup[] | null;
  /** Pointer is over the filter cell — the icons only show up then (or while in use). */
  hovered: boolean;
}>();

const emit = defineEmits<{
  "model-change": [model: ColFilterGroup[] | null];
}>();

const isOpenMenu = ref(false);
const searchInput = ref<HTMLInputElement | null>(null);

const {
  isSelected,
  blankChecked,
  blankLabel,
  blankOptionVisible,
  listSearchTerm,
  filteredOptions,
  optionsView,
  optionsSkeleton,
  loadOptions,
  allSelected,
  someSelected,
  summaryText,
  hasActiveFilter,
  conditionsEnabled,
  conditionDisabledOperators,
  conditionRows,
  toggleSelectAll,
  toggleItem,
  toggleBlank,
  changeConditions,
  flush,
  endEditing,
  clear,
} = useListFilter({
  colKey: () => props.colKey,
  config: () => props.config,
  model: () => props.model,
  onModelChange: (model) => emit("model-change", model),
});

watch(isOpenMenu, (open) => {
  if (open) void loadOptions();
});
</script>

<style module lang="scss">
@use "../../../styles/collapse.scss" as CollapseMixins;
@use "../../../styles/generated/figma_typo_mixins.scss" as FigmaTypoMixins;

@include CollapseMixins.collapse-transitions;

.searchInput {
  width: 100%;
  border: none;
  outline: none;
}

.listDropdown {
  width: 280px;
  max-height: calc(100vh - 300px);
  display: flex;
  flex-direction: column;
}

.listBody {
  display: flex;
  flex-direction: column;
  flex: 1 1 auto;
  min-height: 0;
  padding: var(--space-component-padding-small) var(--space-component-padding-medium)
    var(--space-component-padding-medium);
}

.optionsBlock {
  flex: 1 1 auto;
  min-height: 0;
  grid-template-columns: minmax(0, 1fr);
}

.optionsBlock:not(.collapsibleAnimating) {
  display: flex;
  flex-direction: column;
}

.optionsContent {
  display: flex;
  flex-direction: column;
  gap: var(--space-component-gap-xSmall);
}

.empty {
  @include FigmaTypoMixins.typo-body-medium;
  display: flex;
  align-items: center;
  height: var(--size-xSmall);
  padding: 0 6px;
  color: var(--color-content-secondary);
}

.itemsList {
  display: flex;
  flex-direction: column;
  flex: 1 1 auto;
  min-height: 0;
  overflow-y: auto;
  gap: var(--space-component-gap-xSmall);
}
</style>
